import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MfaCodeStep, MfaEnrollStep } from '../identity/mfa-steps';
import type { TenantBranding } from '@up2d8/contracts';
import { applyTenantTheme, clearTenantTheme, useProductName } from '../../shared/theme';
import { Button, Card, Input } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { LanguageSwitcher } from '../../shared/language-switcher';
import { PIN_LENGTH, PinPad } from './pin-pad';
import {
  chooseLoginMode,
  linkedTenantCode,
  readRememberedLoginMode,
  rememberLoginMode,
  type LoginMode,
} from '../../shared/login-mode';

/** Het toestel onthoudt de bedrijfscode; het personeelsnummer en de pincode niet. */
const TENANT_CODE_KEY = 'up2d8.tenantCode';

export function readRememberedTenantCode(): string {
  try {
    return window.localStorage.getItem(TENANT_CODE_KEY) ?? '';
  } catch {
    // Privémodus of geblokkeerde opslag: dan typt de medewerker de code gewoon.
    return '';
  }
}

export function rememberTenantCode(code: string): void {
  try {
    window.localStorage.setItem(TENANT_CODE_KEY, code);
  } catch {
    // Niets aan te doen, en niets om de aanmelding voor te laten mislukken.
  }
}

/** Hoe lang het scherm wacht op de aanmeldwijze van de organisatie, voor het bij e-mail begint. */
const MODE_WAIT_MS = 2500;

/**
 * Aanmelden in de app. E-mail en wachtwoord (LOG-1) of personeelsnummer en pincode; de andere weg
 * staat altijd als link onder het formulier.
 *
 * Waar het scherm begint, volgt sinds ORG-3 de organisatie als die bekend is: een sportclub begint
 * bij e-mail, een fabriek bij de pincode (de standaard van het type, in Master in te stellen). Hoe
 * het scherm de organisatie kent zonder sessie, staat in `shared/login-mode.ts`.
 */
export function AppLoginPage() {
  const session = useSession();
  const [linked] = useState(() =>
    typeof window === 'undefined' ? null : linkedTenantCode(window.location.search),
  );
  const [mode, setMode] = useState<LoginMode | null>(() => {
    const remembered = readRememberedLoginMode();
    // Zonder link en met een vorige aanmelding op dit toestel is er niets op te vragen.
    if (!linked && remembered) return remembered;
    if (!linked && !readRememberedTenantCode()) return 'email';
    return null;
  });

  useEffect(() => {
    if (mode !== null) return;
    let current = true;
    const code = linked ?? readRememberedTenantCode();
    const fallback = window.setTimeout(() => {
      // Geen antwoord (geen netwerk): niet blijven wachten, dan begint het scherm bij e-mail.
      if (current) setMode(chooseLoginMode({ remembered: readRememberedLoginMode() }));
    }, MODE_WAIT_MS);
    void session.api
      .GET('/api/v1/tenant/branding/{code}', { params: { path: { code } } })
      .then(({ data }) => (data as TenantBranding | undefined)?.loginDefault ?? null)
      .catch(() => null)
      .then((loginDefault) => {
        if (!current) return;
        setMode(
          chooseLoginMode(
            linked
              ? { linked: loginDefault, remembered: readRememberedLoginMode() }
              : { rememberedTenant: loginDefault },
          ),
        );
      });
    return () => {
      current = false;
      window.clearTimeout(fallback);
    };
  }, [mode, linked, session.api]);

  // Een tel niets tonen is beter dan een formulier dat onder de vingers van vorm wisselt.
  if (mode === null) return null;
  return mode === 'email' ? (
    <AppEmailLogin onPin={() => setMode('pin')} />
  ) : (
    <AppPinLogin onEmail={() => setMode('email')} initialTenantCode={linked} />
  );
}

/**
 * E-mail en wachtwoord, zonder bedrijfscode (ACC-1). Zit het adres in meer dan één bedrijf,
 * dan kiest de medewerker daarna waar hij naartoe wil.
 */
function AppEmailLogin({ onPin }: { onPin: () => void }) {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState<{
    choose: { tenantCode: string; name: string }[];
    chooseToken: string;
  } | null>(null);

  const fail = () => {
    // Eén melding voor elke oorzaak; het wachtwoord gaat leeg.
    setChoice(null);
    setPassword('');
    setFailed(true);
  };

  // FBK-12: de code uit de authenticator-app, of eerst 2FA instellen.
  const [mfa, setMfa] = useState<{ tenantCode?: string; failed: boolean } | null>(null);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);

  const next = (result: Awaited<ReturnType<typeof session.login>>, tenantCode?: string) => {
    if (!result) {
      rememberLoginMode('email');
      void navigate({ to: '/app' });
      return;
    }
    if ('mfa' in result) {
      setMfa({ tenantCode, failed: mfa !== null });
      return;
    }
    if ('enrollToken' in result) {
      setEnrollToken(result.enrollToken);
      return;
    }
    // Wie de link of de clubapp van één organisatie volgt (`?org=<code>`) en daar een account
    // heeft, hoeft niet te kiezen: de clubapp van Sint-Michiels opent Sint-Michiels, ook voor wie
    // daarnaast in een ander bedrijf zit. Zonder overeenkomst blijft de keuze staan.
    const linked = typeof window === 'undefined' ? null : linkedTenantCode(window.location.search);
    const match = linked ? result.choose.find((item) => item.tenantCode === linked) : undefined;
    if (match) {
      session
        .loginChoose(result.chooseToken, match.tenantCode)
        .then((chosen) => next(chosen, match.tenantCode))
        .catch(fail);
      return;
    }
    setChoice(result);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailed(false);
    try {
      next(await session.login({ email: email.trim(), password }));
    } catch {
      fail();
    }
  };

  const submitCode = async (second: { totpCode?: string; recoveryCode?: string }) => {
    try {
      next(
        await session.login({
          email: email.trim(),
          password,
          tenantCode: mfa?.tenantCode,
          ...second,
        }),
        mfa?.tenantCode,
      );
    } catch {
      setMfa((current) => (current ? { ...current, failed: true } : current));
    }
  };

  const choose = async (tenantCode: string) => {
    if (!choice) return;
    try {
      const result = await session.loginChoose(choice.chooseToken, tenantCode);
      setChoice(null);
      next(result, tenantCode);
    } catch {
      fail();
    }
  };

  if (enrollToken || mfa) {
    return (
      <AppAuthFrame title={enrollToken ? t('mfa.enrollTitle') : t('mfa.title')} intro="">
        {/* De stappen komen uit het beheer (admin-kit); `a-scope` geeft ze hun stijl. */}
        <div className="a-scope">
          {enrollToken ? (
            <MfaEnrollStep
              enrollToken={enrollToken}
              onDone={() => void navigate({ to: '/app' })}
              onExpired={() => {
                setEnrollToken(null);
                setPassword('');
              }}
            />
          ) : (
            <MfaCodeStep
              busy={session.loading}
              failed={mfa?.failed}
              onSubmit={(second) => void submitCode(second)}
            />
          )}
        </div>
      </AppAuthFrame>
    );
  }

  if (choice) {
    return (
      <AppAuthFrame title={t('login.chooseTitle')} intro="">
        <div className="flex flex-col gap-element" data-testid="app-login-choose">
          {choice.choose.map((item) => (
            <Button
              key={item.tenantCode}
              variant="secondary"
              className="w-full"
              disabled={session.loading}
              onClick={() => void choose(item.tenantCode)}
            >
              {item.name}
            </Button>
          ))}
        </div>
      </AppAuthFrame>
    );
  }

  return (
    <AppAuthFrame title={t('appLogin.title')} intro={t('appLogin.emailIntro')}>
      <form onSubmit={submit} className="flex flex-col gap-element" noValidate>
        <Input
          label={t('login.email')}
          name="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label={t('login.password')}
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {failed ? (
          <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger">
            {t('appLogin.emailFailed')}
          </p>
        ) : null}

        <Button
          type="submit"
          loading={session.loading}
          disabled={email.trim() === '' || password === ''}
          className="w-full"
        >
          {t('login.submit')}
        </Button>
      </form>

      <p className="mt-element text-center text-content text-text-secondary">
        <Link to="/admin/forgot-password" className="text-brand-primary underline">
          {t('login.forgot')}
        </Link>
      </p>
      <p className="text-center text-content text-text-secondary">
        <button
          type="button"
          className="text-brand-primary underline"
          data-testid="login-with-pin"
          onClick={onPin}
        >
          {t('appLogin.withPin')}
        </button>
      </p>
      {/* Zelfregistratie (Stap 34, REG-2): wie een poster of een bericht met een code kreeg. */}
      <p className="text-center text-content text-text-secondary">
        <Link to="/app/join" className="text-brand-primary underline" data-testid="login-have-code">
          {t('appLogin.haveCode')}
        </Link>
      </p>
    </AppAuthFrame>
  );
}

/**
 * Aanmelden op de werkvloer: bedrijfscode, personeelsnummer en pincode.
 *
 * De bedrijfscode staat op het toestel opgeslagen — die typt niemand elke ochtend opnieuw.
 * Het personeelsnummer en de pincode niet: een telefoon wordt wel eens gedeeld of
 * vergeten, en dan hoort hij niet vanzelf open te gaan.
 */
function AppPinLogin({
  onEmail,
  initialTenantCode,
}: {
  onEmail: () => void;
  /** De code uit de link (`?org=`), die wint van de code die het toestel onthoudt (ORG-3). */
  initialTenantCode?: string | null;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();

  const [tenantCode, setTenantCode] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);

  useEffect(() => {
    setTenantCode(initialTenantCode ?? readRememberedTenantCode());
  }, [initialTenantCode]);

  /**
   * Zodra de bedrijfscode volledig is, komen de naam en het logo van dat bedrijf op het
   * scherm. Dat is geen versiering: elk aanmeldscherm lijkt op elk ander, en een medewerker
   * die zijn code verkeerd onthoudt, hoort dat hier te zien en niet na drie mislukte
   * pogingen. Er zit een halve seconde vertraging op, zodat er niet bij elke toetsaanslag een
   * verzoek uitgaat.
   */
  const code = tenantCode.trim();
  useEffect(() => {
    if (code.length < 3) {
      setBranding(null);
      return;
    }
    let current = true;
    const timer = window.setTimeout(() => {
      void session.api
        .GET('/api/v1/tenant/branding/{code}', { params: { path: { code } } })
        .then(({ data }) => {
          if (current) {
            const next = (data as TenantBranding | undefined) ?? null;
            setBranding(next);
            // De kleuren van de klant zodra zijn code klopt: een medewerker hoort te zien
            // dat hij bij zijn eigen bedrijf uitkomt, niet alleen aan het logo (Stap 43).
            if (next) applyTenantTheme(next.theme);
            else clearTenantTheme();
          }
        });
    }, 500);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [code, session.api]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    let result: string | null;
    try {
      result = await session.loginWithPin({
        tenantCode: tenantCode.trim(),
        employeeNumber: employeeNumber.trim(),
        pin,
      });
    } catch {
      // Geen netwerk: de aanroep gooit. Zonder deze vangst ziet de medewerker niets gebeuren.
      result = 'failed';
    }

    if (result === null) {
      rememberTenantCode(tenantCode.trim());
      rememberLoginMode('pin');
      void navigate({ to: '/app' });
      return;
    }
    setPin('');
    setError(result === 'locked' ? t('appLogin.locked') : t('appLogin.failed'));
  };

  return (
    <AppAuthFrame title={t('appLogin.title')} intro={t('appLogin.intro')} branding={branding}>
      <form onSubmit={submit} className="flex flex-col gap-element" noValidate>
        <Input
          label={t('login.tenantCode')}
          name="tenantCode"
          autoComplete="organization"
          value={tenantCode}
          onChange={(event) => setTenantCode(event.target.value)}
          required
        />
        <Input
          label={t('appLogin.employeeNumber')}
          hint={t('appLogin.employeeNumberHint')}
          name="employeeNumber"
          inputMode="numeric"
          autoComplete="username"
          value={employeeNumber}
          onChange={(event) => setEmployeeNumber(event.target.value)}
          required
        />
        <PinPad value={pin} onChange={setPin} label={t('appLogin.pin')} />

        {error ? (
          <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          loading={session.loading}
          disabled={pin.length !== PIN_LENGTH || employeeNumber.trim() === ''}
          className="w-full"
        >
          {t('login.submit')}
        </Button>
      </form>

      <p className="mt-element text-center text-content text-text-secondary">
        {t('appLogin.noAccountYet')}{' '}
        <Link to="/app/activate" className="text-brand-primary underline">
          {t('appLogin.toActivate')}
        </Link>
      </p>
      <p className="text-center text-content text-text-secondary">
        <button
          type="button"
          className="text-brand-primary underline"
          data-testid="login-with-email"
          onClick={onEmail}
        >
          {t('appLogin.withEmail')}
        </button>
      </p>
      {/* Zelfregistratie (Stap 34, REG-2): wie een poster of een bericht met een code kreeg. */}
      <p className="text-center text-content text-text-secondary">
        <Link to="/app/join" className="text-brand-primary underline" data-testid="login-have-code">
          {t('appLogin.haveCode')}
        </Link>
      </p>
    </AppAuthFrame>
  );
}

/**
 * De schil van de aanmeld- en activatieschermen in de app-zone.
 *
 * `data-zone="app"` schakelt de AA-tokenset in: 16px basistekst en 48px raakvlakken
 * (ADR-0018). Eén kolom, want dit scherm wordt op een telefoon gebruikt.
 */
export function AppAuthFrame({
  title,
  intro,
  children,
  branding,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
  /** Naam en logo van het bedrijf, zodra de bedrijfscode bekend is. */
  branding?: TenantBranding | null;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  return (
    <div data-zone="app" className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto flex w-full max-w-md flex-col gap-section">
        <header className="text-center">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={t('media.logoAlt', { name: branding.displayName })}
              className="mx-auto mb-element max-h-16 max-w-[12rem] object-contain"
            />
          ) : null}
          <p className="text-caption uppercase tracking-[0.12em] text-text-muted">
            {/* De naam die de klant koos, niet de naam uit het handelsregister (Extra 1). */}
            {branding?.displayName ?? productName}
          </p>
          <h1 className="mt-1 font-heading text-page-title font-semibold text-text-primary">
            {title}
          </h1>
          <p className="mt-2 text-content text-text-secondary">{intro}</p>
        </header>

        <Card>{children}</Card>

        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}
